/**
 * This module is largely inspired by signup_welcome_v2.html / the rendered wtform for signup.
 * It draws inspiration from the existing two-step signup flow as we have a similar need:
 * after a user enters their email, we may redirect the user to a different tenant depending on the domain.
 *
 * NOTE: Our backend forms are dynamic based on config flags, while this front-end implementation is not.
 * It assumes that we have correctly configured the Public AI Trial tenant.
 *
 * Notably:
 *   ENABLE_SIGNUP_WITH_FIRST_LAST_NAME must be true.
 */
import {Select} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import * as BenchlingUtil from 'BenchlingUtil';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import UserConstants from 'constants/UserConstants';
import * as csrf from 'CSRF';
import staticURL from 'lib/staticURL';
import {LLMSpinner} from 'llm/loading/LLMLoadingMessage';
import notifications from 'notifications';
import {ZINDEX_FLOATING_UI} from 'styles/StyleConstants';
import LegacyFieldError from 'ui-library/bs/LegacyFieldError';
import useIsNotUnmounted from 'ui-library/hooks/useIsNotUnmounted';
import useIsPhoneWidth from 'ui-library/hooks/useIsPhoneWidth';
import useOnMount from 'ui-library/hooks/useOnMount';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Modal from 'ui-library/modals/Modal';
import * as net from 'util/Net';

const REDIRECT_DELAY_MS = 5000;
export interface Props {
  onRequestClose: () => void;
  trigger: string;
}

interface EmailValidationErrorResponse {
  userMessage?: string;
  redirectUrl?: string;
  redirectText?: string;
  errors?: {
    email?: Array<string>;
    next?: Array<string>;
  };
}

// TODO: Log an error if a an unknown field is returned from the backend.
type SignUpField =
  | 'email'
  | 'next'
  | 'first_name'
  | 'last_name'
  | 'username'
  | 'password'
  | 'title'
  | 'department'
  | 'role'
  | 'field'
  | 'marketing_consent';
interface SignupErrorResponse {
  errors?: {
    [key in SignUpField]?: Array<string>;
  };
}

enum AITrialRole {
  SCIENTIST = 'Scientist',
  DATA_SCIENTIST_ML = 'Data Science & ML',
  EXECUTIVE = 'Executive',
  PROGRAM_MANAGEMENT = 'Program Management',
  OTHER = 'Other',
}

function getMobileFriendlyRole(role: AITrialRole): string {
  if (role === AITrialRole.PROGRAM_MANAGEMENT) {
    return 'Program Mgmt';
  }
  return role;
}

enum AITrialDepartment {
  RESEARCH = 'Research',
  DEVELOPMENT = 'Development',
  MANUFACTURING = 'Manufacturing',
  IT_SOFTWARE_ENG = 'IT / Software Engineering',
  REGULATORY_MEDICAL_WRITER = 'Regulatory / Medical Writing',
  LEGAL = 'Legal',
}

const DEPARTMENTS_WITH_ROLES = [
  AITrialDepartment.RESEARCH,
  AITrialDepartment.DEVELOPMENT,
  AITrialDepartment.MANUFACTURING,
];

function EmailFieldError({errorResponse}: {errorResponse: EmailValidationErrorResponse}): JSX.Element | null {
  // Render the first error if it exists.
  const error = errorResponse.errors?.email?.[0] ?? errorResponse.userMessage;
  if (!error) {
    return null;
  }
  // An error may span multiple lines
  const lines = error.split('\n');
  return (
    <>
      {lines.map((line: string, idx: number) => (
        <span
          key={idx}
          className={classNames(
            'legacy-field-error',
            css(
              // If a redirect exists, it's less of an error and more of a notice.
              errorResponse.redirectUrl != null && styles.nonError,
              // Bold with increased margin
              idx === 0 && styles.firstLine,
              // Normal weight
              idx > 0 && styles.otherLines
            )
          )}
        >
          {line}
        </span>
      ))}
    </>
  );
}

/* @package - exported for testing */
export const _CONFIGURATION = {
  withinPortal: true,
};

/**
 * A front-end port of signup_welcome_v2.html with rendered AITrialEmailForm
 */
function StepOneEmailForm({
  email,
  setEmail,
  onSuccess,
}: {
  email: string;
  setEmail: (email: string) => void;
  onSuccess: () => void;
}): JSX.Element {
  const isNotUnmounted = useIsNotUnmounted();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isPhoneWidth = useIsPhoneWidth();
  const inputStyle = useInputStyle({important: false, isPhoneWidth});

  const [errorResponse, setErrorResponse] = useState<EmailValidationErrorResponse>({
    errors: {},
    redirectUrl: undefined,
    redirectText: undefined,
  });

  const onSubmitEmail = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setIsSubmitting(true);
      // TODO(BNCH-135333): Also send to Marketo.
      net
        .postJSONPromise({
          url: '/1/api/llm/auth:validate-signup-email',
          data: {
            email,
            csrf_token: csrf.token,
            next: window.location.pathname,
          },
        })
        .then(() => {
          trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.SUBMITTED_EMAIL_IN_AI_TRIAL_SIGNUP_MODAL, {
            email,
            accepted: true,
          });
          onSuccess();
        })
        .catch(async (xhrRes) => {
          trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.SUBMITTED_EMAIL_IN_AI_TRIAL_SIGNUP_MODAL, {
            email,
            accepted: false,
            statusCode: xhrRes.statusCode,
            redirectUrl: xhrRes.responseJSON?.redirectUrl,
            error: notifications.getFriendlyError(xhrRes),
            errors: xhrRes.responseJSON?.errors,
          });

          if (xhrRes.responseJSON) {
            setErrorResponse(xhrRes.responseJSON);
          } else {
            notifications.notifyError(xhrRes);
          }
          if (xhrRes.responseJSON.redirectUrl) {
            await new Promise((resolve) => {
              setTimeout(resolve, REDIRECT_DELAY_MS);
            });
            if (isNotUnmounted.current) {
              BenchlingUtil.setLocationHref(xhrRes.responseJSON.redirectUrl);
            }
          } else {
            setIsSubmitting(false);
          }
        });
    },
    [email, onSuccess, isNotUnmounted]
  );

  return (
    <form onSubmit={onSubmitEmail}>
      <div className="row" style={{marginBottom: '-15px'}}>
        <div className="col-md-12">
          <div className="form-group">
            <div style={{height: '40px'}}>
              <div style={{float: 'left', width: '75%'}}>
                <input
                  autoFocus={true}
                  name="email"
                  placeholder="Email"
                  style={{...inputStyle, borderRadius: '4px 0 0 4px'}}
                  type="text"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                  }}
                />
              </div>
              <div style={{float: 'right', width: '25%'}}>
                <button
                  type="submit"
                  name="continue"
                  style={{height: '40px', lineHeight: '12px'}}
                  className="button button--inline button--large button--bioblue"
                  disabled={isSubmitting}
                >
                  Continue
                </button>
              </div>
            </div>
            <EmailFieldError errorResponse={errorResponse} />
          </div>
        </div>
      </div>
      <div className="row u-spacing-top text-center">
        {errorResponse.redirectUrl ? (
          <p className={css(styles.redirecting)}>
            <LLMSpinner /> Redirecting to{' '}
            <a href={errorResponse.redirectUrl}>{errorResponse.redirectText ?? errorResponse.redirectUrl}</a>{' '}
            shortly...
          </p>
        ) : (
          <>
            <span>Already have an account?</span>{' '}
            <a href={BenchlingUtil.setURLParams('/signin', {next: window.location.pathname})}>Sign In</a>
          </>
        )}
      </div>
    </form>
  );
}

/**
 * Provide styles to apply to inputs, handling height / font-size.
 *
 * This helper is compatible with both React `style` attribute and Mantine's `styles` prop.
 * The former does not support `important`, and this tends to not be necessary, as these styles are
 * added directly to the element, thus having high specificity.  The latter does support `important`,
 * and ends up being useful as the Mantine styles have lower specificity than the styles in benchling.less.
 *
 * @param important - Whether to use `!important` to ensure the styles have sufficient specificity.
 * @param isPhoneWidth - Whether the input is on a phone width.
 * @param mobileFontSize - The font size to use on a phone width. Defaults to 16px to avoid iOS form zoom.
 *   See: https://css-tricks.com/16px-or-larger-text-prevents-ios-form-zoom/
 */
function useInputStyle({
  important,
  isPhoneWidth,
  mobileFontSize = '16px',
}: {important?: boolean; mobileFontSize?: string; isPhoneWidth?: boolean} = {}): {
  fontSize?: string;
  height?: string;
} {
  return {
    height: important ? '40px !important' : '40px',
    ...(isPhoneWidth ? {fontSize: important ? `${mobileFontSize} !important` : mobileFontSize} : {}),
  };
}

/**
 * Manage state for the backend "title" field, combined from role and department frontend fields.
 * This is a mild hack as introducing new fields on the backend is a non-trivial change.
 */
function useTitle(): {
  titleInputs: {
    role: AITrialRole | null;
    dept: AITrialDepartment | null;
  };
  setRole: (role: AITrialRole | null) => void;
  setDept: (dept: AITrialDepartment | null) => void;
  isRoleRequired: boolean;
  formData: {
    title: string;
    role: string;
    department: string;
  };
} {
  const [role, setRole] = useState<AITrialRole | null>(null);
  const [dept, setDept] = useState<AITrialDepartment | null>(null);
  const isRoleRequired = DEPARTMENTS_WITH_ROLES.includes(dept as AITrialDepartment);

  // Clear the role field if it's not required.
  useEffect(() => {
    if (role && !isRoleRequired) {
      setRole(null);
    }
  }, [isRoleRequired, role]);

  // Combine the role and department fields into a single title field.
  const title = useMemo(() => {
    const parts = [];
    if (dept != null) {
      parts.push(dept);
    }
    if (role != null) {
      parts.push(role);
    }
    return parts.join(' - ');
  }, [role, dept]);

  const formData = useMemo(
    () => ({
      title,
      // HACK: Backend always assumes role is required; we specify N/A to adhere to that, which works because
      // role is discarded on the backend; only title is used.
      role: isRoleRequired ? (role ?? '') : 'N/A',
      department: dept ?? '',
    }),
    [title, role, dept, isRoleRequired]
  );

  return {titleInputs: {role, dept}, setRole, setDept, isRoleRequired, formData};
}

/**
 * A front-end port of signup_welcome_v2.html with rendered RegistrationForm.
 *
 * Role / Department, however, is unique to this component, stored in the existing title field.
 */
function StepTwoSignupForm({email, onSuccess}: {email: string; onSuccess: () => void}): JSX.Element {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorResponse, setErrorResponse] = useState<SignupErrorResponse>({errors: {}});

  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const {titleInputs, setRole, setDept, isRoleRequired, formData: titleFormData} = useTitle();
  const [marketingConsent, setMarketingConsent] = useState(true);

  const isPhoneWidth = useIsPhoneWidth();
  const inputStyle = useInputStyle({important: false, isPhoneWidth});
  const selectInputStyle = useInputStyle({
    important: true,
    mobileFontSize: isRoleRequired ? '14px' : '16px',
    isPhoneWidth,
  });
  const onSubmitSignup = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setIsSubmitting(true);
      await net
        .postJSONPromise({
          url: '/1/api/llm/auth:signup',
          data: {
            email,
            csrf_token: csrf.token,
            next: window.location.pathname,
            first_name: firstName,
            last_name: lastName,
            username,
            password,
            ...titleFormData,
            marketing_consent: marketingConsent,
            name_required: true,
            // See backend: affiliation_required is necesssary to trigger syncing user metadata to Segment
            affiliation_required: true,
            affiliation: UserConstants.PROCLAIMED_COMMERCIAL,
            // TODO: Do we need to do something like setLastDigitalTouchToken? See: #1199
            last_digital_touch: '',
          },
        })
        .then(() => {
          trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.SUBMITTED_FORM_IN_AI_TRIAL_SIGNUP_MODAL, {
            email,
            accepted: true,
          });
          // Wait for the analytics event to be sent before redirecting.
          setTimeout(() => {
            onSuccess();
          }, 250);
        })
        .catch((xhrRes) => {
          trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.SUBMITTED_FORM_IN_AI_TRIAL_SIGNUP_MODAL, {
            email,
            accepted: false,
            statusCode: xhrRes.statusCode,
            error: notifications.getFriendlyError(xhrRes),
            errors: xhrRes.responseJSON.errors,
          });
          if (xhrRes.responseJSON.errors) {
            setErrorResponse(xhrRes.responseJSON);
          } else {
            notifications.notifyError(xhrRes);
          }
          setIsSubmitting(false);
        });
    },
    [email, firstName, lastName, username, password, marketingConsent, titleFormData, onSuccess]
  );

  // If for some reason, the role and department fields did not trigger validation errors, then show the
  // title field error if it exists.
  const titleError =
    !errorResponse.errors?.role && !errorResponse.errors?.department
      ? errorResponse.errors?.title
      : undefined;

  return (
    <form onSubmit={onSubmitSignup} autoComplete="off" className="signup-form homepageAuthForm">
      <div className="getting-started-fields">
        <input type="hidden" name="name_required" value="1" />
        <div style={{height: '55px'}}>
          <div className="form-group">
            <div style={{float: 'left', width: '49%'}}>
              <input
                autoFocus={true}
                placeholder="First Name"
                style={inputStyle}
                type="text"
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                }}
              />
              <LegacyFieldError message={errorResponse.errors?.first_name} />
            </div>
            <div style={{float: 'right', width: '49%'}}>
              <input
                placeholder="Last Name"
                style={inputStyle}
                type="text"
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                }}
              />
              <LegacyFieldError message={errorResponse.errors?.last_name} />
            </div>
          </div>
        </div>
      </div>
      <div>
        <div className="form-group">
          <input
            placeholder="Username"
            style={inputStyle}
            type="text"
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
            }}
          />
          <LegacyFieldError message={errorResponse.errors?.username} />
        </div>
        <div className="form-group">
          <input
            autoComplete="off"
            placeholder="Password"
            style={inputStyle}
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
            }}
          />
          <LegacyFieldError message={errorResponse.errors?.password} />
        </div>
      </div>
      <div className="form-group">
        <div style={{display: 'flex', gap: '1%', overflow: 'hidden'}}>
          <div
            style={{
              // If role is not required, then push it off the screen (see overflow: hidden above)
              width: isRoleRequired ? '49%' : '100%',
              flex: '0 0 auto',
              minWidth: 0,
              transition: 'width 0.2s ease',
            }}
          >
            <Select
              placeholder="Department"
              allowDeselect={true}
              size="md"
              data={Object.values(AITrialDepartment).map((dept) => ({value: dept, label: dept}))}
              value={titleInputs.dept}
              withinPortal={_CONFIGURATION.withinPortal}
              maxDropdownHeight={500}
              zIndex={ZINDEX_FLOATING_UI}
              onChange={(value) => {
                setDept(value as AITrialDepartment);
              }}
              styles={{input: selectInputStyle, dropdown: {minWidth: 'max-content'}}}
            />
            <LegacyFieldError message={errorResponse.errors?.department ?? titleError} />
          </div>
          <div
            style={{
              flex: '0 0 auto',
              width: '50%',
              minWidth: 0,
              transition: 'display 0.2s allow-discrete',
              display: isRoleRequired ? 'block' : 'none',
            }}
          >
            <Select
              placeholder="Role"
              allowDeselect={true}
              size="md"
              data={Object.values(AITrialRole).map((role) => ({
                value: role,
                // Despite using fontSize 14px, the roles tend to be long enough to overflow, so use sn
                // shorter label if necessary.
                label: isPhoneWidth ? getMobileFriendlyRole(role) : role,
              }))}
              value={titleInputs.role}
              withinPortal={_CONFIGURATION.withinPortal}
              zIndex={ZINDEX_FLOATING_UI}
              maxDropdownHeight={500}
              onChange={(value) => {
                setRole(value as AITrialRole);
              }}
              styles={{
                input: selectInputStyle,
                dropdown: {
                  // Ensures the dropdown is at least as wide as the widest vslue; otherwise, it breaks words.
                  minWidth: 'max-content',
                },
              }}
            />
            <LegacyFieldError message={errorResponse.errors?.role} />
          </div>
        </div>
      </div>
      <div className="form-group">
        <div className="checkbox">
          <label>
            <input
              id="marketing_consent"
              name="marketing_consent"
              type="checkbox"
              checked={marketingConsent}
              onChange={(e) => {
                setMarketingConsent(e.target.checked);
              }}
            />
            <div className="marketingCheckbox">
              <label htmlFor="marketing_consent">
                Send me marketing emails, including company resources and news
              </label>
            </div>
          </label>
        </div>
        <LegacyFieldError message={errorResponse.errors?.marketing_consent} />
      </div>
      <div className="submit-wrapper">
        <button
          type="submit"
          disabled={isSubmitting}
          className="button button--fullWidth button--medium button--bioblue"
        >
          Create account
        </button>
      </div>

      <span className="help-block text-center">
        By clicking “Create account” you agree to our <a href="/terms-of-service">Terms of Service</a> and our{' '}
        <a href="/privacy-policy">Privacy Policy</a>.
      </span>
    </form>
  );
}

export default function AITrialSignupModal(props: Props): JSX.Element {
  const [email, setEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(false);
  useOnMount(() => {
    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.OPENED_AI_TRIAL_SIGNUP_MODAL, {
      trigger: props.trigger,
    });
  });
  return (
    <Modal
      medium={true}
      onRequestClose={props.onRequestClose}
      suppressHeader={true}
      wrapInForm={false}
      bodyPadded={false}
      closeable={true}
    >
      <div className={css(styles.closeButton)}>
        <BenchlingIcon className="close" icon="close" data-dismiss="modal" />
      </div>
      <div className={css(styles.content)}>
        <div className="text-center">
          <a href="/" className="logo">
            <img
              width="115"
              height="24"
              src={staticURL('/static/img/bioblue_wordmark_500.png')}
              className="brand-benchling-logo"
            />
          </a>
          <h2 className={css(styles.title)}>Create an account to use Benchling AI</h2>
        </div>
        {!isEmailValid ? (
          <StepOneEmailForm
            email={email}
            setEmail={setEmail}
            onSuccess={() => {
              setIsEmailValid(true);
            }}
          />
        ) : (
          <StepTwoSignupForm
            email={email}
            onSuccess={() => {
              BenchlingUtil.reload();
            }}
          />
        )}
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  closeButton: {
    margin: '20px',
    position: 'absolute',
    right: '0',
    top: '0',
  },
  content: {
    padding: '40px',
  },
  title: {
    margin: '20px 0',
  },
  redirecting: {
    clear: 'both',
  },
  nonError: {
    color: 'black',
    marginTop: 8,
  },
  firstLine: {
    marginTop: 8,
    marginBottom: 0,
  },
  otherLines: {
    marginBottom: 0,
    fontWeight: 400,
  },
});
